import React, { useState } from "react";
import {
  Activity,
  ArrowLeft,
  CalendarDays,
  Database,
  Footprints,
  MapPinned,
  Route,
  ShieldCheck,
  ShoppingBasket,
  SquareParking,
} from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/layout/Navbar";
import CaseStudyCta from "../components/layout/CaseStudyCta";
import "../styles/frogn.css";

// Figures on this page come from the Drøbak and Frogn analysis app (July 2026).

const highlights = [
  { label: "Municipality", value: "115 km²" },
  { label: "Measured zones", value: "5.2 km²" },
  { label: "Presence readings", value: "Every 10 min" },
  { label: "Visits to the zones in July", value: "60,060", tag: "Measured" },
];

const evidence = [
  {
    Icon: Activity,
    type: "Measured",
    title: "Counted in July 2026",
    body: "Presence every 10 minutes in two Drøbak zones through July, the SIM country of visitors, and Statens vegvesen roadside counts.",
  },
  {
    Icon: Route,
    type: "Modelled",
    title: "Estimated with travel, routing and retail models",
    body: "Daily trips and travel modes, road traffic, walking times to services, grocery catchments and sites for a new grocery store.",
  },
  {
    Icon: Database,
    type: "Open data",
    title: "Public data from SSB, OpenStreetMap and Statens vegvesen",
    body: "Population, roads, services, shops, parking, and public traffic counters across the municipality.",
  },
];

// Measured visits per day across both zones, 1–31 July 2026.
const julyVisits = [
  1040, 658, 1314, 1960, 2509, 2021, 435, 1184, 1102, 1837, 2367, 2226, 1251, 1600, 2379, 1772,
  2786, 842, 1849, 3147, 2060, 2309, 2142, 3998, 2356, 2828, 1319, 3267, 1537, 1153, 2812,
];
const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
// 1 July 2026 is a Wednesday.
const dayName = (index) => DAY_NAMES[(3 + index) % 7];
const isWeekend = (index) => ["Saturday", "Sunday"].includes(dayName(index));
const CHART_MAX = 4000;
const BUSIEST = julyVisits.indexOf(Math.max(...julyVisits));
const fmt = (n) => n.toLocaleString("en-GB");

const measuredStats = [
  { value: "60,060", label: "visits to the two zones in July" },
  { value: "3,998", label: "visits on Friday 24 July, the busiest day" },
  { value: "+13%", label: "higher daily average at weekends: 2,117 visits, against 1,875 on weekdays" },
  { value: "15:00", label: "busiest hour of the day for visitors, over July as a whole" },
  { value: "23", label: "SIM countries seen; after Norway, the most common were Germany, Italy and Ukraine" },
  { value: "7,423", label: "people present at the quietest time, against 6,113 residents in the two zones" },
];

const services = [
  { name: "Bus stop", share: 93 },
  { name: "Playground or park", share: 86 },
  { name: "School", share: 77 },
  { name: "Kindergarten", share: 76 },
  { name: "Grocery", share: 59 },
  { name: "GP or clinic", share: 59 },
  { name: "Pharmacy", share: 36 },
];

const parking = [
  { label: "Visitor cars at the peak", value: 1249, type: "Modelled", kind: "demand" },
  { label: "Parking spaces in the two zones", value: 684, type: "Open data", kind: "supply" },
  { label: "Parking spaces in the wider town", value: 2511, type: "Open data", kind: "supply" },
];
const PARKING_MAX = 2511;

const calibration = [
  { counter: "Frogntunnelen", counted: "18,288", modelled: "16,704", diff: "−9%" },
  { counter: "Oslofjordtunnelen", counted: "14,664", modelled: "17,418", diff: "+19%" },
  { counter: "Ullerudsletta (Fv152)", counted: "13,284", modelled: "12,114", diff: "−9%" },
];

// Hero mosaic. Each map is cropped in CSS to the area that carries data, so the baked-in
// legends and credits fall outside the frame: the captions restate the legends and the
// credit line under the mosaic carries the attribution.
// crop: x = left edge and w = width of the visible strip, cy = vertical centre (source px).
const heroMaps = [
  {
    key: "access",
    src: "/frogn/service-access.jpg",
    size: [1000, 799],
    crop: { x: 245, w: 596, cy: 400 },
    tag: "Modelled",
    title: "Everyday services within a 15-minute walk",
    alt: "Map of Frogn with each populated 250 m cell drawn as a dot, coloured by how many of seven everyday services are within a 15-minute walk and sized by its residents. The large dark green dots that reach most services cluster around Drøbak; the pale dots that reach few are spread across the rest of the municipality.",
  },
  {
    key: "zones",
    src: "/frogn/measured-zones.jpg",
    size: [960, 640],
    crop: { x: 290, w: 532, cy: 357 },
    tag: "Measured",
    title: "Two Drøbak zones, counted every 10 minutes in July",
    alt: "Map of central Drøbak with the two measured zones outlined: Sentrum around the town centre and Zone 2 to the south of it. The modelled 10- and 20-minute walks from the town square are shaded, and mapped parking is drawn as blue circles.",
  },
  {
    key: "roads",
    src: "/frogn/road-flows.jpg",
    size: [1060, 813],
    crop: { x: 330, w: 670, cy: 330 },
    tag: "Modelled",
    title: "Daily road flows, scaled to roadside counts",
    alt: "Dark map of modelled daily road flows around Drøbak, with wider, brighter lines on busier roads. Green dots mark the three counters on the roads into Drøbak that the flows are scaled to; amber dots mark counters further out, where the model differs from the count by more than 30%.",
  },
];
// Colour ramp of the walking-access map legend, sampled from the image (0 of 7 to 7 of 7).
const ACCESS_RAMP = ["#f6fcb4", "#dbf2a4", "#b4e190", "#80ce7a", "#51b36a", "#31924d", "#12753e", "#005a30"];

const cropStyle = ({ size: [width, height], crop: { x, w, cy } }) => ({
  left: `${(-100 * x) / w}%`,
  width: `${(100 * width) / w}%`,
  transform: `translateY(${(-100 * cy) / height}%)`,
});

const HeroMap = ({ map, children }) => (
  <figure className={`frogn-atlas-tile frogn-atlas-tile--${map.key}`}>
    <div className="frogn-atlas-window">
      <img src={map.src} alt={map.alt} style={cropStyle(map)} />
    </div>
    <figcaption>
      <p>
        <span className={`frogn-tag frogn-tag--${map.tag.toLowerCase()}`}>{map.tag}</span>
        {map.title}
      </p>
      {children}
    </figcaption>
  </figure>
);

const DailyVisitsChart = () => {
  const [active, setActive] = useState(null);
  const readout =
    active === null ? (
      <>Average day: weekdays <strong>1,875</strong> visits, weekends <strong>2,117</strong></>
    ) : (
      <>{dayName(active)} {active + 1} July: <strong>{fmt(julyVisits[active])}</strong> visits</>
    );

  return (
    <figure className="frogn-chart">
      <figcaption className="frogn-chart-head">
        <span className="frogn-chip">Measured</span>
        <strong>Visits per day in the two zones, 1–31 July 2026</strong>
      </figcaption>
      <p className="frogn-chart-readout" aria-hidden="true">{readout}</p>
      <div
        className="frogn-daily"
        role="img"
        aria-label="Column chart of visits per day to the two Drøbak zones in July 2026. Daily visits range from 435 on Tuesday 7 July to 3,998 on Friday 24 July, the busiest day. Weekend days are shown in a darker green."
      >
        <div className="frogn-daily-grid">
          {[4000, 3000, 2000, 1000, 0].map((tick) => (
            <div key={tick} style={{ bottom: `${(tick / CHART_MAX) * 100}%` }}>
              <span>{fmt(tick)}</span>
            </div>
          ))}
        </div>
        <div className="frogn-daily-bars" onMouseLeave={() => setActive(null)}>
          {julyVisits.map((visits, index) => (
            <div
              key={index}
              className={`frogn-daily-col${isWeekend(index) ? " is-weekend" : ""}${active === index ? " is-active" : ""}`}
              onMouseEnter={() => setActive(index)}
            >
              <i style={{ height: `${(visits / CHART_MAX) * 100}%` }} />
              {index === BUSIEST && (
                <em style={{ bottom: `calc(${(visits / CHART_MAX) * 100}% + 6px)` }}>
                  Fri 24 Jul · {fmt(visits)}
                </em>
              )}
            </div>
          ))}
        </div>
        <div className="frogn-daily-x">
          {[1, 8, 15, 22, 29].map((day) => (
            <span key={day} style={{ left: `${((day - 0.5) / julyVisits.length) * 100}%` }}>
              {day} Jul
            </span>
          ))}
        </div>
      </div>
      <ul className="frogn-legend">
        <li><i className="frogn-swatch frogn-swatch--weekday" aria-hidden="true" />Monday to Friday</li>
        <li><i className="frogn-swatch frogn-swatch--weekend" aria-hidden="true" />Saturday and Sunday</li>
      </ul>
      <details className="frogn-chart-table">
        <summary>Show the daily figures</summary>
        <table>
          <thead>
            <tr><th scope="col">Date</th><th scope="col">Visits</th></tr>
          </thead>
          <tbody>
            {julyVisits.map((visits, index) => (
              <tr key={index}>
                <th scope="row">{dayName(index)} {index + 1} July</th>
                <td>{fmt(visits)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  );
};

const FrognPage = () => (
  <div className="frogn-page">
    <Navbar />
    <main className="pt-16">
      <header className="frogn-hero">
        <div className="frogn-hero-inner">
          <div className="frogn-hero-copy">
            <Link to="/projects" className="frogn-back"><ArrowLeft size={15} aria-hidden="true" /> Projects</Link>
            <p className="frogn-kicker">Case study · Frogn municipality, Norway</p>
            <h1>Visitors, traffic, walking and shopping in <span>Drøbak</span> and Frogn</h1>
            <p className="frogn-deck">We counted aggregated mobile-network presence in two Drøbak zones every 10 minutes through July 2026, then joined it to models of road traffic, walking access and grocery spend for the rest of Frogn.</p>
            <ul className="frogn-meta">
              <li><MapPinned size={15} aria-hidden="true" />Drøbak and Frogn</li>
              <li><CalendarDays size={15} aria-hidden="true" />July 2026</li>
              <li><Database size={15} aria-hidden="true" />Measured, modelled, and open data</li>
              <li><ShieldCheck size={15} aria-hidden="true" />Aggregated mobile-network data</li>
            </ul>
          </div>
          <div className="frogn-atlas">
            <div className="frogn-atlas-grid">
              <HeroMap map={heroMaps[0]}>
                <div className="frogn-atlas-legend">
                  <span className="sr-only">Dot colour runs from</span>
                  <span>0 of 7</span>
                  <span className="frogn-atlas-ramp" aria-hidden="true">
                    {ACCESS_RAMP.map((colour) => <i key={colour} style={{ background: colour }} />)}
                  </span>
                  <span className="sr-only">to</span>
                  <span>7 of 7 services</span>
                  <span className="frogn-atlas-legend-note">Dot size: residents in the 250&nbsp;m cell</span>
                </div>
              </HeroMap>
              <div className="frogn-atlas-side">
                <HeroMap map={heroMaps[1]} />
                <HeroMap map={heroMaps[2]} />
              </div>
            </div>
            <p className="frogn-atlas-credit">
              Maps ©&nbsp;<a href="https://carto.com/attributions" target="_blank" rel="noreferrer">CARTO</a> ·{" "}
              ©&nbsp;<a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap contributors</a>
            </p>
          </div>
        </div>
      </header>

      <section className="frogn-highlights" aria-label="Case study highlights">
        <div className="frogn-highlights-inner">
          {highlights.map(({ label, value, tag }) => (
            <div key={label}>
              <small>{label}</small>
              <strong>{value}{tag && <>{" "}<em>{tag}</em></>}</strong>
            </div>
          ))}
        </div>
      </section>

      <section className="frogn-intro frogn-wrap">
        <div>
          <h2>Access, traffic and retail affect each other</h2>
        </div>
        <div>
          <p>Our walking model puts half of Frogn's residents more than 19 minutes' walk from a pharmacy, and 10,651 people beyond a 15-minute walk. Those trips are more likely to be made by car, which adds to local traffic and parking.</p>
          <p>A new grocery store brings both spend and traffic. In our retail model, the best site we tested keeps about 29 MNOK a year of spend in Frogn and adds 2,059 car and foot trips a week on local roads and paths.</p>
          <p>In central Drøbak, the modelled number of visitor cars at the July peak was almost twice the number of parking spaces in the two measured zones.</p>
        </div>
      </section>

      <section className="frogn-evidence">
        <div className="frogn-wrap">
          <div className="frogn-evidence-head">
            <p className="frogn-label">Data</p>
            <h2>What we counted, what we modelled, and what came from open data</h2>
            <p>Every output is labelled so a decision maker can see where observation ends and modelling begins.</p>
          </div>
          <div className="frogn-evidence-grid">
            {evidence.map(({ Icon, type, title, body }) => (
              <article key={type}><Icon size={22} /><small>{type}</small><h3>{title}</h3><p>{body}</p></article>
            ))}
          </div>
        </div>
      </section>

      <section className="frogn-measured frogn-wrap">
        <div className="frogn-measured-head">
          <p className="frogn-label">Visitors</p>
          <h2>Visits to two Drøbak zones in July 2026</h2>
          <p>We counted aggregated mobile-network presence every 10 minutes from 1 to 31 July in two zones: Sentrum in the town centre, and Zone 2 to the south of it. The counts come from one operator's network and are scaled to an estimate for all networks.</p>
        </div>
        <div className="frogn-measured-grid">
          <DailyVisitsChart />
          <div>
            <p className="frogn-stats-tag"><span className="frogn-chip">Measured</span>Both zones, 1–31 July 2026</p>
            <dl className="frogn-stats">
              {measuredStats.map(({ value, label }) => (
                <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      <section className="frogn-parking">
        <div className="frogn-wrap frogn-parking-inner">
          <figure className="frogn-image-card frogn-zone-map">
            <img
              src="/frogn/measured-zones.jpg"
              alt="Map of central Drøbak showing the two measured zones, Sentrum and Zone 2, the areas within a 10- and 20-minute walk of the town square, and mapped parking drawn as blue circles sized by capacity."
            />
            <figcaption>The two measured zones, with modelled 10- and 20-minute walks from the square and mapped parking sized by capacity</figcaption>
          </figure>
          <div>
            <p className="frogn-label">Parking</p>
            <h2>Parking in the centre at the July peak</h2>
            <p>At the busiest point in July, presence in the two zones was 3,997 people above the resident level (measured). At a 56% car share and 1.8 people per car, that is about 1,249 visitor cars (modelled). The two zones have 684 mapped parking spaces, and the wider town has 2,511 (open data).</p>
            <figure className="frogn-hbars frogn-hbars--parking">
              <figcaption><SquareParking size={17} aria-hidden="true" />Visitor cars and parking spaces at the July peak</figcaption>
              <ul>
                {parking.map(({ label, value, type, kind }) => (
                  <li key={label}>
                    <span className="frogn-hbars-label">{label}<span className="frogn-chip frogn-chip--quiet">{type}</span></span>
                    <span className="frogn-hbars-track" aria-hidden="true">
                      <i className={`frogn-hbars-fill--${kind}`} style={{ width: `${(value / PARKING_MAX) * 100}%` }} />
                    </span>
                    <strong>{fmt(value)}</strong>
                  </li>
                ))}
              </ul>
            </figure>
            <p className="frogn-small">The 56% car share comes from the national travel survey and describes residents on an ordinary weekday. We applied it to the July visitors as an assumption.</p>
          </div>
        </div>
      </section>

      <section className="frogn-traffic frogn-wrap">
        <div className="frogn-traffic-copy">
          <p className="frogn-label">Traffic</p>
          <h2>Road traffic modelled, then scaled to roadside counts</h2>
          <p>We routed the modelled daily trips over the real road network, then scaled the flows to match what Statens vegvesen counted in July at the three counters on the roads into Drøbak. Counters further out sit on main roads carrying long-distance traffic that starts outside the modelled area, so we left them out of the fit. They show amber on the map, where the model differs from the count by more than 30%.</p>
          <div className="frogn-calibration">
            <table>
              <caption><Route size={17} aria-hidden="true" />Calibration fit on the roads into Drøbak, vehicles a day</caption>
              <thead>
                <tr><th scope="col">Counter</th><th scope="col">Counted</th><th scope="col">Modelled</th><th scope="col">Difference</th></tr>
              </thead>
              <tbody>
                {calibration.map(({ counter, counted, modelled, diff }) => (
                  <tr key={counter}><th scope="row">{counter}</th><td>{counted}</td><td>{modelled}</td><td>{diff}</td></tr>
                ))}
              </tbody>
            </table>
            <p>Because the model is scaled to these three counters, the differences show the quality of the fit. They do not test the model independently.</p>
          </div>
        </div>
        <figure className="frogn-image-card frogn-image-card--dark">
          <img
            src="/frogn/road-flows.jpg"
            alt="Dark map of calibrated vehicles per day on the roads around Drøbak, with line width showing the flow. The three counters on the roads into Drøbak are green, within 30% of the model: Frogntunnelen 18,288 counted (−9%), Oslofjordtunnelen 14,664 counted (+19%) and Ullerudsletta 13,284 counted (−9%). The counters further out are amber, outside 30%."
          />
          <figcaption><span>Modelled + open data</span> Calibrated daily road flows; green counters are within 30% of the model, amber counters are outside it</figcaption>
        </figure>
      </section>

      <section className="frogn-access">
        <div className="frogn-wrap">
          <div className="frogn-access-head">
            <p className="frogn-label">Walking and shopping</p>
            <h2>Who can walk to what, and where grocery spend goes</h2>
            <p>30% of Frogn's residents reach all seven everyday services within a 15-minute walk. For groceries, about three-quarters of the spend in the modelled catchment, which covers 65,436 people, goes to competing stores outside Frogn.</p>
          </div>
          <div className="frogn-access-grid">
            <article>
              <figure className="frogn-hbars frogn-hbars--services">
                <figcaption>Share of Frogn's residents within a 15-minute walk</figcaption>
                <ul>
                  {services.map(({ name, share }) => (
                    <li key={name}>
                      <span className="frogn-hbars-label">{name}</span>
                      <span className="frogn-hbars-track" aria-hidden="true"><i style={{ width: `${share}%` }} /></span>
                      <strong>{share}%</strong>
                    </li>
                  ))}
                </ul>
              </figure>
              <div>
                <Footprints size={20} />
                <p className="frogn-card-tags"><small>Walking access</small><span className="frogn-chip">Modelled + open data</span></p>
                <h3>Walking time to seven everyday services from every populated 250&nbsp;m cell</h3>
                <p>We routed walks over OpenStreetMap footpaths at 4.8&nbsp;km/h and weighted each cell by its residents on the SSB population grid. 30% of residents can choose between two or more grocery stores within a 15-minute walk.</p>
              </div>
            </article>
            <article>
              <figure className="frogn-grocery-map">
                <img
                  src="/frogn/grocery-catchment.jpg"
                  alt="Map of eight grocery stores around Drøbak. Circle area is each store's modelled annual spend captured, labelled from 15 to 154 MNOK a year; the largest circles, 154, 120 and 102 MNOK, sit north-east of the town centre."
                />
                <figcaption>Circle area is each store's modelled annual capture in MNOK. Two smaller stores in the north of Frogn fall outside the frame.</figcaption>
              </figure>
              <div>
                <ShoppingBasket size={20} />
                <p className="frogn-card-tags"><small>Grocery catchments</small><span className="frogn-chip">Modelled + open data</span></p>
                <h3>What each grocery store draws</h3>
                <p>Each 250&nbsp;m population cell splits its grocery spend across nearby stores by store size and drive time. Frogn's ten stores capture about 672 MNOK a year between them. Store floor areas are averages for each store format.</p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="frogn-scenario frogn-wrap">
        <figure className="frogn-image-card">
          <img
            src="/frogn/candidate-sites.jpg"
            alt="Map of Frogn with candidate grocery sites on an 800 m grid, each square shaded by the net spend a new store there would keep in Frogn, from 0 to 29 MNOK a year. The best site, starred near Fagerstrand north of Drøbak, keeps 29 MNOK a year in Frogn. Dots mark existing stores."
          />
          <figcaption><span>Modelled scenario</span> Candidate sites across populated Frogn</figcaption>
        </figure>
        <div>
          <p className="frogn-label">A new store</p>
          <h2>Scoring sites for a new grocery store</h2>
          <p>We placed a new store on each point of an 800&nbsp;m grid across populated Frogn and reran the retail model each time. Each site is scored by the extra spend that stays in Frogn once the losses at existing stores are netted off. The score covers grocery spend only; it does not include zoning, land or building costs.</p>
          <div className="frogn-scenario-facts">
            <div><strong>29 MNOK/year</strong><span>Net extra spend kept in Frogn by the best site (modelled)</span></div>
            <div><strong>2,059/week</strong><span>Extra car and foot trips on local roads and paths (modelled)</span></div>
            <div><strong>35 MNOK/year</strong><span>Spend captured by the new store (modelled)</span></div>
            <div><strong>6 MNOK/year</strong><span>Taken from existing Frogn stores (modelled)</span></div>
          </div>
        </div>
      </section>

      <aside className="frogn-note" aria-labelledby="frogn-note-title">
        <strong id="frogn-note-title">About the figures</strong>
        <ul>
          <li><b>Measured:</b> aggregated mobile-network presence and SIM country in two Drøbak zones, every 10 minutes from 1 to 31 July 2026, and Statens vegvesen roadside counts for July. Presence and SIM-country counts come from one operator's devices and are scaled to an all-network estimate using that operator's market share. The share is a domestic figure, so counts for foreign SIMs carry more uncertainty than Norwegian ones.</li>
          <li><b>Modelled:</b> daily trips and travel modes, road flows, walking times, grocery spend and the new-store site scores. These are estimates. Travel modes come from the national travel survey and describe residents on an ordinary weekday.</li>
          <li><b>Open data:</b> SSB 250 m population grid (2026), OpenStreetMap roads, footpaths, shops, services and parking, and Statens vegvesen traffic data (NLOD).</li>
        </ul>
        <p>Map visuals © CARTO and © OpenStreetMap contributors.</p>
      </aside>

      <CaseStudyCta
        question="Planning parking, services or a new store in a town that fills up with visitors?"
        body="The population grid, road network, shops, services and roadside counts in this study are open data that cover all of Norway. Measured presence needs zones drawn around the places you want to understand and a measurement period."
        extra={
          <Link to="/platform/privacy" className="font-semibold text-brand-700 underline underline-offset-4 hover:text-brand-600">
            How we process mobile-network data
          </Link>
        }
      />
    </main>
  </div>
);

export default FrognPage;
